import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import type { GuideMeta } from '../types';
import { useGuideText } from '../useGuideText';

interface GuidePagerProps {
  guides: GuideMeta[];
  activeId: string;
  onSelect: (id: string) => void;
}

const BTN =
  'flex max-w-[48%] items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700';

export const GuidePager: React.FC<GuidePagerProps> = ({
  guides,
  activeId,
  onSelect,
}) => {
  const { t } = useTranslation('HelpGuide');
  const { title } = useGuideText();
  const index = guides.findIndex((g) => g.id === activeId);
  const previous = index > 0 ? guides[index - 1] : undefined;
  const next =
    index >= 0 && index < guides.length - 1 ? guides[index + 1] : undefined;
  return (
    <div className="mt-8 flex items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
      <button
        type="button"
        className={BTN}
        disabled={!previous}
        onClick={() => previous && onSelect(previous.id)}
      >
        <FiChevronLeft size={16} />
        <span className="flex min-w-0 flex-col text-left leading-tight">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('pager.previous')}
          </span>
          {previous && (
            <span className="truncate text-gray-800 dark:text-gray-100">
              {title(previous.id)}
            </span>
          )}
        </span>
      </button>
      <button
        type="button"
        className={BTN}
        disabled={!next}
        onClick={() => next && onSelect(next.id)}
      >
        <span className="flex min-w-0 flex-col text-left leading-tight">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('pager.next')}
          </span>
          {next && (
            <span className="truncate text-gray-800 dark:text-gray-100">
              {title(next.id)}
            </span>
          )}
        </span>
        <FiChevronRight size={16} />
      </button>
    </div>
  );
};
